import { createParameter } from "lib/parameters/parameters";
import { parameterIndex } from "lib/parameters/parameters";
import { sortParameters } from "lib/parameters/parameters";
import { Parameter, ParametersState } from "lib/parameters/types";

type DifferenceArgs = {
  difference: string[];
  nextCode: string;
  nextKeys: string[];
  parameters: ParametersState;
  prevKeys: string[];
};

type HandleDifference = (
  args: DifferenceArgs
) => { nextParameters: Parameter[]; updatedCode: string | undefined };

export const getParameterKeys = (code: string) => {
  let match: RegExpExecArray | null;
  // RegExpExecArray looks like this:
  // [0]: The full string of characters matched
  // [1], ...[n]: The parenthesized substring matches (unlimited number)
  // index: The 0-based index of the match in the string (here: in the code).
  // indices: An array where each entry represents a substring match
  // input:  The original string that was matched against (here: code)

  const matches: string[] = [];
  const regexp = /(\{{.*?}})/g;

  while ((match = regexp.exec(code)) !== null) {
    const parameterTag = match[0];
    // Strip brackets from tag: {{param}} -> param
    const parameterKey = parameterTag.substring(2, parameterTag.length - 2);
    matches.push(parameterKey);
  }

  return matches;
};

const findIndexByKey = (keys: string[], key: string) =>
  keys.findIndex((k) => k === key);

const findParameterByKey = (ps: Parameter[], key: string) =>
  ps.find((p) => p.key === key);

const removeParameter = (ps: Parameter[], p: Parameter) =>
  ps.splice(parameterIndex(ps, p), 1);

export const getSymmetricDifference = (arr1: any[], arr2: any[]) => {
  const x = arr2.filter((x) => !arr1.includes(x));
  const y = arr1.filter((x) => !arr2.includes(x));

  // Get unique values from the symmetric difference between arrays
  return [...new Set(x.concat(y))];
};

export const compareCode = (prevCode: string, nextCode: string) => {
  // Find parameters in prev code
  const prevKeys = getParameterKeys(prevCode);
  // Find parameters in next code
  const nextKeys = getParameterKeys(nextCode);
  // Get difference between prev and next parameters
  const difference = getSymmetricDifference(prevKeys, nextKeys);

  return { prevKeys, nextKeys, difference };
};

export const handleDifference: HandleDifference = ({
  difference,
  nextCode,
  nextKeys,
  parameters,
  prevKeys,
}) => {
  let nextParameters: Parameter[];
  let updatedCode: string | undefined;

  if (nextKeys.length > prevKeys.length) {
    nextParameters = createParameters(difference, parameters);
  } else if (nextKeys.length < prevKeys.length) {
    nextParameters = removeParameters(difference, parameters);
  } else {
    const result = updateParameters({
      difference,
      nextCode,
      nextKeys,
      prevKeys,
      parameters,
    });

    updatedCode = result.updatedCode;
    nextParameters = result.nextParameters;
  }

  return { nextParameters, updatedCode };
};

export const createParameters = (
  difference: string[],
  parameters: ParametersState
) => {
  const nextParameters = [...parameters.defaults];
  const removed = [...parameters.removed];

  for (const item of difference) {
    // Check if parameter was recently removed (stored on parameters state)
    const removedParameter = findParameterByKey(removed, item);

    if (removedParameter) {
      // Drop parameter from removed
      removeParameter(removed, removedParameter);
      // Restore removed parameter
      nextParameters.push(removedParameter);
    } else {
      // Create parameter
      const { parameter } = createParameter(nextParameters, item);
      nextParameters.push(parameter);
    }
  }

  if (parameters.removed.length !== removed.length) {
    // Update parameters state
    parameters.setRemoved(removed);
  }

  // Return alphabetically sorted parameters
  return sortParameters(nextParameters);
};

export const removeParameters = (
  difference: string[],
  parameters: ParametersState
) => {
  const nextParameters = [...parameters.defaults];
  // Create an array to keep track of removed parameters
  const removedParameters = [];

  for (const item of difference) {
    const parameter = findParameterByKey(nextParameters, item);

    if (parameter) {
      // Remove parameter from nextParameters
      removeParameter(nextParameters, parameter);
      // Add parameter to removed array so it can be restored later
      removedParameters.push(parameter);
    }
  }

  if (removedParameters.length > 0) {
    parameters.setRemoved(removedParameters);
  }

  return nextParameters;
};

export const updateParameters: HandleDifference = ({
  difference,
  nextCode,
  nextKeys,
  prevKeys,
  parameters,
}) => {
  const nextParameters = [...parameters.defaults];

  for (const item of difference) {
    // Get index of the change
    const index = findIndexByKey(nextKeys, item);
    // Get the parameter that was changed
    const prevParameter = findParameterByKey(nextParameters, prevKeys[index]);

    if (prevParameter) {
      // Preserve parameter metadata, change key
      const nextParameter = { ...prevParameter, key: item };
      nextParameters.push(nextParameter);
    } else {
      // If previous parameter is not found, we may end up with
      // an obsolete parameter: there is still one in the params
      // inputs list, but there is none in the query editor code
      const itemIndex = findIndexByKey(prevKeys, item);

      const obsoleteParameter = findParameterByKey(
        nextParameters,
        prevKeys[itemIndex]
      );

      if (obsoleteParameter) {
        removeParameter(nextParameters, obsoleteParameter);
      }
    }
  }

  return { nextParameters, updatedCode: nextCode };
};
